import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as adminService from '../../services/adminService';
import './AdminSubscriptionsPage.css';

interface SubscriptionUser {
  email: string;
  username: string;
  avatarUrl: string | null;
}

interface SubscriptionProduct {
  name: string;
  billingPeriod: string;
}

interface Subscription {
  id: string;
  userId: string;
  status: string;
  platform: string;
  startedAt: string;
  expiresAt: string;
  cancelledAt: string | null;
  trialEndsAt: string | null;
  stripeSubscriptionId: string | null;
  user: SubscriptionUser;
  product: SubscriptionProduct;
}

interface SubscriptionListResponse {
  data: Subscription[];
  total: number;
  page: number;
  limit: number;
}

const STATUS_OPTIONS = ['all', 'active', 'trialing', 'cancelled', 'expired'];
const PLATFORM_OPTIONS = ['all', 'web', 'ios', 'android'];
const PLAN_OPTIONS = ['all', 'monthly', 'yearly'];

const AdminSubscriptionsPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();

  const [data, setData] = useState<SubscriptionListResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const [status, setStatus] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [plan, setPlan] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page, limit };
      if (status !== 'all') params.status = status;
      if (platform !== 'all') params.platform = platform;
      if (plan !== 'all') params.plan = plan;
      if (debouncedSearch) params.search = debouncedSearch;

      const result = await adminService.getSubscriptions(params);
      setData(result);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, status, platform, plan, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [status, platform, plan]);

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusClass = (s: string): string => {
    switch (s.toLowerCase()) {
      case 'active': return 'admin-sub-badge--active';
      case 'trialing': return 'admin-sub-badge--trialing';
      case 'cancelled': return 'admin-sub-badge--cancelled';
      case 'expired': return 'admin-sub-badge--expired';
      default: return 'admin-sub-badge--inactive';
    }
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 0;
  const startItem = data ? (page - 1) * limit + 1 : 0;
  const endItem = data ? Math.min(page * limit, data.total) : 0;

  // Summary stats
  const countByStatus = (s: string) =>
    data?.data.filter((sub) => sub.status.toLowerCase() === s).length ?? 0;

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, i) => (
      <tr key={i} className="admin-sub-skeleton-row">
        <td><div className="admin-sub-skeleton-cell" style={{ width: 180 }} /></td>
        <td><div className="admin-sub-skeleton-cell" style={{ width: 60 }} /></td>
        <td><div className="admin-sub-skeleton-cell" style={{ width: 70 }} /></td>
        <td><div className="admin-sub-skeleton-cell" style={{ width: 50 }} /></td>
        <td><div className="admin-sub-skeleton-cell" style={{ width: 80 }} /></td>
        <td><div className="admin-sub-skeleton-cell" style={{ width: 80 }} /></td>
        <td><div className="admin-sub-skeleton-cell" style={{ width: 60 }} /></td>
      </tr>
    ));

  return (
    <div className="admin-sub-page">
      <h1 className="admin-sub-title">{t('subscriptions.title', 'Subscriptions')}</h1>

      {/* Filter Bar */}
      <div className="admin-sub-filters">
        <div className="admin-sub-filter-group">
          <label className="admin-sub-filter-label">
            {t('subscriptions.status', 'Status')}
          </label>
          <select
            className="admin-sub-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt === 'all'
                  ? t('subscriptions.filterAll', 'All')
                  : t(`subscriptions.status_${opt}`, opt.charAt(0).toUpperCase() + opt.slice(1))}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-sub-filter-group">
          <label className="admin-sub-filter-label">
            {t('subscriptions.platform', 'Platform')}
          </label>
          <select
            className="admin-sub-select"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
          >
            {PLATFORM_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt === 'all'
                  ? t('subscriptions.filterAll', 'All')
                  : opt.charAt(0).toUpperCase() + opt.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-sub-filter-group">
          <label className="admin-sub-filter-label">
            {t('subscriptions.plan', 'Plan')}
          </label>
          <select
            className="admin-sub-select"
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
          >
            {PLAN_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt === 'all'
                  ? t('subscriptions.filterAll', 'All')
                  : t(`subscriptions.plan_${opt}`, opt.charAt(0).toUpperCase() + opt.slice(1))}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-sub-filter-group admin-sub-filter-search">
          <label className="admin-sub-filter-label">
            {t('subscriptions.search', 'Search')}
          </label>
          <div className="admin-sub-search-wrapper">
            <svg className="admin-sub-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="admin-sub-search-input"
              placeholder={t('subscriptions.searchPlaceholder', 'Email or username...')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {data && !loading && (
        <div className="admin-sub-stats">
          <div className="admin-sub-stat">
            <span className="admin-sub-stat-value admin-sub-stat--active">
              {countByStatus('active')}
            </span>
            <span className="admin-sub-stat-label">
              {t('subscriptions.activeCount', 'Active')}
            </span>
          </div>
          <div className="admin-sub-stat">
            <span className="admin-sub-stat-value admin-sub-stat--trialing">
              {countByStatus('trialing')}
            </span>
            <span className="admin-sub-stat-label">
              {t('subscriptions.trialingCount', 'Trialing')}
            </span>
          </div>
          <div className="admin-sub-stat">
            <span className="admin-sub-stat-value admin-sub-stat--cancelled">
              {countByStatus('cancelled')}
            </span>
            <span className="admin-sub-stat-label">
              {t('subscriptions.cancelledCount', 'Cancelled')}
            </span>
          </div>
          <div className="admin-sub-stat-total">
            {t('subscriptions.showingRange', 'Showing {{start}}-{{end}} of {{total}}', {
              start: startItem,
              end: endItem,
              total: data.total,
            })}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="admin-sub-table-wrapper">
        <table className="admin-sub-table">
          <thead>
            <tr>
              <th>{t('subscriptions.colUser', 'User')}</th>
              <th>{t('subscriptions.colPlan', 'Plan')}</th>
              <th>{t('subscriptions.colStatus', 'Status')}</th>
              <th>{t('subscriptions.colPlatform', 'Platform')}</th>
              <th>{t('subscriptions.colStarted', 'Started')}</th>
              <th>{t('subscriptions.colExpires', 'Expires')}</th>
              <th>{t('subscriptions.colActions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              renderSkeletonRows()
            ) : data && data.data.length > 0 ? (
              data.data.map((sub) => (
                <tr key={sub.id} className="admin-sub-row">
                  <td>
                    <div className="admin-sub-user-cell">
                      <div className="admin-sub-avatar">
                        {sub.user.avatarUrl ? (
                          <img src={sub.user.avatarUrl} alt="" />
                        ) : (
                          <span>{(sub.user.username || sub.user.email).charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="admin-sub-user-info">
                        <span className="admin-sub-username">{sub.user.username || '-'}</span>
                        <span className="admin-sub-email">{sub.user.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="admin-sub-plan-name">
                      {sub.product?.name || '-'}
                    </span>
                    <span className="admin-sub-plan-period">
                      {sub.product?.billingPeriod || ''}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-sub-badge ${getStatusClass(sub.status)}`}>
                      {sub.status}
                    </span>
                  </td>
                  <td className="admin-sub-platform">{sub.platform || '-'}</td>
                  <td className="admin-sub-date">{formatDate(sub.startedAt)}</td>
                  <td className="admin-sub-date">{formatDate(sub.expiresAt)}</td>
                  <td>
                    <button
                      className="admin-sub-view-btn"
                      onClick={() => navigate(`/admin/subscriptions/${sub.id}`)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      {t('subscriptions.view', 'View')}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7}>
                  <div className="admin-sub-empty">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                      <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                    <p>{t('subscriptions.empty', 'No subscriptions found')}</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && totalPages > 1 && (
        <div className="admin-sub-pagination">
          <button
            className="admin-sub-page-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 19l-7-7 7-7" />
            </svg>
            {t('subscriptions.prev', 'Previous')}
          </button>
          <span className="admin-sub-page-info">
            {t('subscriptions.pageInfo', 'Page {{page}} of {{total}}', {
              page,
              total: totalPages,
            })}
          </span>
          <button
            className="admin-sub-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('subscriptions.next', 'Next')}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionsPage;
